import { getDbSafe } from './firebaseConfig';
import { collection, query, orderBy, limit, where, getDocs, QueryConstraint } from 'firebase/firestore';
import { computeCommunityTrendingScore } from '../utils/communityTrending';

export type CommunityFilter = 'trending' | 'newest' | 'posts' | 'members' | 'for_you' | 'university';

// The Trending filter sorts by the same rate-based score that awards the
// Trending badge, so the tab and the badge can never disagree about what is
// actually hot. See src/utils/communityTrending.ts.
const computeTrendingScore = computeCommunityTrendingScore;

export const CommunityDiscoveryService = {
    /** Fetch all active university tribes. Used by the Universities filter tab. */
    async getUniversityTribes(limitCount: number = 50) {
        const db = await getDbSafe();
        const q = query(
            collection(db, 'tribes'),
            where('tribeType', '==', 'university'),
            where('status', '==', 'Active'),
            limit(limitCount)
        );
        try {
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.warn('[CommunityDiscovery] University query failed:', e);
            return [];
        }
    },

    async getCommunities(
        type: 'forums' | 'tribes',
        filter: CommunityFilter,
        userTags: string[] = [],
        limitCount: number = 20,
        /** Optional viewer-scoped filter (muted words), supplied by the caller
         *  from useContentFilter. Applied to every return path below, before the
         *  list is trimmed — these queries fetch ~100 and return ~20, so there is
         *  ample headroom to absorb the removals. */
        isMutedGroup?: (group: any) => boolean,
    ) {
        const applyMute = <T,>(rows: T[]): T[] =>
            isMutedGroup ? rows.filter(r => !isMutedGroup(r)) : rows;
        let constraints: QueryConstraint[] = [];

        switch (filter) {
            case 'university':
                // Delegate to dedicated function so ExploreScreen needs no special-casing
                return applyMute(await CommunityDiscoveryService.getUniversityTribes(limitCount));

            case 'trending': {
                // Trending = combination of most active (posts) + newest (recency)
                // Fetch all active, then sort client-side with composite score
                constraints.push(limit(limitCount));
                constraints = [
                    where('status', '==', 'Active'),
                    ...(type === 'forums' ? [where('isPublic', '==', true)] : []),
                    limit(100)
                ];

                const db2 = await getDbSafe();
                const trendingQ = query(collection(db2, type), ...constraints);
                try {
                    const snapshot = await getDocs(trendingQ);
                    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    results.sort((a: any, b: any) => computeTrendingScore(b) - computeTrendingScore(a));
                    return applyMute(results).slice(0, limitCount);
                } catch (e) {
                    console.warn('[CommunityDiscovery] Trending query failed, using fallback:', e);
                    // Fall through to the normal query path below
                    constraints = [orderBy('createdAt', 'desc')];
                }
                break;
            }
            case 'newest':
                // User Definition: "obviosuly time made"
                constraints.push(orderBy('createdAt', 'desc'));
                break;
            case 'posts':
                // User Definition: "Most active is just the highest frequency of posts"
                // Implementation: Use postCount (Total Posts) as proxy since posts7d is not yet populated
                constraints.push(orderBy('postCount', 'desc'));
                break;
            case 'members':
                // User Definition: "Largest is obviously highest member count"
                constraints.push(orderBy('memberCount', 'desc'));
                break;
            case 'for_you': {
                // Match on BOTH the group's explicit tags (weight 2) AND its
                // aggregated member tags (weight 1), so "general" groups whose
                // members share the viewer's interests surface even without an
                // explicit tag. Explicit tags rank higher. Scored client-side
                // because Firestore can't rank by array-intersection size or
                // array-contains-any across two fields.
                const dbFy = await getDbSafe();
                const forYouQ = query(
                    collection(dbFy, type),
                    where('status', '==', 'Active'),
                    ...(type === 'forums' ? [where('isPublic', '==', true)] : []),
                    limit(100)
                );
                try {
                    const snap = await getDocs(forYouQ);
                    const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    const tagSet = new Set(userTags);
                    const tagsLower = userTags.map(t => String(t).toLowerCase());
                    const scoreOf = (g: any) => {
                        const explicit = [...(g.tags || []), ...(g.identities || [])];
                        const member = g.memberTags || [];
                        const nameLower = (g.name || '').toLowerCase();
                        // group name match (3) > explicit tag (2) > member tag (1)
                        const nameHits = tagsLower.filter(t => t && nameLower.includes(t)).length;
                        return nameHits * 3
                             + explicit.filter((t: string) => tagSet.has(t)).length * 2
                             + member.filter((t: string) => tagSet.has(t)).length;
                    };
                    const byTrending = (a: any, b: any) => computeTrendingScore(b) - computeTrendingScore(a);

                    if (userTags.length > 0) {
                        const related = fetched
                            .map(g => ({ g, s: scoreOf(g) }))
                            .filter(x => x.s > 0)
                            .sort((a, b) => (b.s - a.s) || byTrending(a.g, b.g))
                            .map(x => x.g);
                        // Never leave the tab empty — fall back to trending order.
                        return applyMute(related.length > 0 ? related : fetched.sort(byTrending)).slice(0, limitCount);
                    }
                    return applyMute(fetched.sort(byTrending)).slice(0, limitCount);
                } catch (e) {
                    console.warn('[CommunityDiscovery] for_you scoring failed, using newest:', e);
                    constraints.push(orderBy('createdAt', 'desc'));
                    break;
                }
            }
            default:
                constraints.push(orderBy('createdAt', 'desc'));
        }

        constraints.push(limit(limitCount));

        // Add basic status check and public visibility
        constraints = [
            where('status', '==', 'Active'),
            ...(type === 'forums' ? [where('isPublic', '==', true)] : []), // ✅ Only public forums
            ...constraints
        ];

        const db = await getDbSafe();
        const q = query(collection(db, type), ...constraints);

        try {
            // 🔒 PATCH: For 'for_you' filter, we MUST sort client-side by intersection count
            // Firestore cannot sort by "number of matching elements in array"
            if (filter === 'for_you') {
                const snapshot = await getDocs(q);
                let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Sort by number of matching tags
                results.sort((a: any, b: any) => {
                    const tagsA = a.identities || a.tags || [];
                    const tagsB = b.identities || b.tags || [];
                    
                    // Count intersections with userTags
                    const scoreA = tagsA.filter((t: string) => userTags.includes(t)).length;
                    const scoreB = tagsB.filter((t: string) => userTags.includes(t)).length;
                    
                    // Sort descending by score
                    return scoreB - scoreA;
                });
                
                return applyMute(results);
            }

            const snapshot = await getDocs(q);
            return applyMute(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error: any) {
            console.error("Discovery Query Error:", error);

            // Log the index link if present - CRITICAL for debugging
            if (error.message && error.message.includes('index')) {
                console.warn("MISSING INDEX LINK:", error.message);
            }

            // Fallback 1: Client-Side Sorting
            // Fetch a larger batch of "Active" groups and sort them in memory
            try {
                console.warn(`Attempting Fallback: Client-side sort for '${filter}'`);
                
                // 1. Fetch active groups (limit 100 to be safe but get good spread)
                const fallbackQ = query(
                    collection(db, type),
                    where('status', '==', 'Active'),
                    limit(100)
                );
                
                const snapshot = await getDocs(fallbackQ);
                let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 2. Manual Sort
                switch (filter) {
                    case 'trending':
                        results.sort((a: any, b: any) => computeTrendingScore(b) - computeTrendingScore(a));
                        break;
                    case 'members':
                        results.sort((a: any, b: any) => (b.memberCount || 0) - (a.memberCount || 0));
                        break;
                    case 'posts':
                        results.sort((a: any, b: any) => (b.postCount || 0) - (a.postCount || 0));
                        break;
                    case 'newest':
                        results.sort((a: any, b: any) => {
                             const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                             const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                             return dateB.getTime() - dateA.getTime();
                        });
                        break;
                    case 'for_you':
                        // Simple intersection count
                        results.sort((a: any, b: any) => {
                             const tagsA = a.tags || [];
                             const tagsB = b.tags || [];
                             const scoreA = tagsA.filter((t: string) => userTags.includes(t)).length;
                             const scoreB = tagsB.filter((t: string) => userTags.includes(t)).length;
                             return scoreB - scoreA;
                        });
                        break;
                }

                return applyMute(results).slice(0, limitCount);

            } catch (fallbackError: any) {
                 console.error("Client-side sort fallback failed:", fallbackError);
                 
                 // Fallback 2: Hail Mary - just get recent items
                 try {
                     const rawQ = query(
                         collection(db, type),
                         orderBy('createdAt', 'desc'),
                         limit(limitCount)
                     );
                     const snapshot = await getDocs(rawQ);
                     return applyMute(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                 } catch (rawError) {
                     return [];
                 }
            }
        }
    }
};
